<template>
  <div class="p-3 sm:p-4 pb-20 sm:pb-8 max-w-xl mx-auto space-y-3 w-full">
    <!-- Google Map View / Customer Location -->
    <div class="relative rounded-2xl overflow-hidden shadow-xs border border-gray-100 bg-gray-100 h-48 sm:h-52 w-full">
      <div id="start-job-google-map" class="w-full h-full"></div>
      <div v-if="!hasCoordinates"
        class="absolute inset-0 bg-gray-100 flex flex-col items-center justify-center text-center p-4">
        <v-icon color="grey lighten-1" size="32">mdi-map-marker-off</v-icon>
        <span class="text-xs text-gray-500 font-medium mt-1">No map coordinates available</span>
      </div>
    </div>

    <!-- Clock-in Warning Alert -->
    <div v-if="clockInWarningVisible"
      class="p-3 bg-rose-50 border border-rose-200 flex items-center rounded-xl text-xs text-rose-800 gap-2">
      <v-icon small color="rose darken-2">mdi-alert-circle-outline</v-icon>
      <p class="mb-0">Please Clock-In first then you can start your job.</p>
    </div>

    <!-- Top Appointment & Action Header -->
    <div class="flex items-center justify-between pt-1">
      <p class="text-sky-700 text-xs font-bold mb-0">
        Appointment: {{ appointmentDateTime }}
      </p>
      <div class="flex items-center gap-2">
        <v-btn v-if="customerMobile" icon small :color="$vuetify.theme.currentTheme.employeePrimary" class="bg-indigo-50" :href="'tel:' + customerMobile">
          <v-icon small :color="$vuetify.theme.currentTheme.employeePrimary">mdi-phone</v-icon>
        </v-btn>
        <v-btn v-if="customerAddress" icon small color="teal" class="bg-teal-50" @click="openGoogleMaps">
          <v-icon small color="teal">mdi-directions</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Collapsible Accordions matching Mobile App -->
    <v-expansion-panels v-model="activePanels" multiple class="rounded-2xl overflow-hidden space-y-2.5">
      <!-- Panel 1: Status Details -->
      <v-expansion-panel class="rounded-2xl border border-gray-100 shadow-2xs before:hidden">
        <v-expansion-panel-header class="font-bold text-xs sm:text-sm text-gray-800 py-3">
          <div class="flex items-center gap-2">
            <v-icon small color="indigo">mdi-list-status</v-icon>
            <span>Status</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="text-xs text-gray-600">
          <div class="flex items-center justify-between py-1.5 border-b border-gray-100">
            <span class="text-gray-500">Task Status</span>
            <span class="font-extrabold text-indigo-700">{{ taskStatus }}</span>
          </div>
          <div v-if="taskData && taskData.description" class="py-1.5 text-gray-600">
            <span class="text-gray-500 block mb-0.5">Description</span>
            <span class="font-medium">{{ taskData.description }}</span>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Panel 2: Customer Details -->
      <v-expansion-panel class="rounded-2xl border border-gray-100 shadow-2xs before:hidden">
        <v-expansion-panel-header class="font-bold text-xs sm:text-sm text-gray-800 py-3">
          <div class="flex items-center gap-2">
            <v-icon small color="indigo">mdi-account-details-outline</v-icon>
            <span>Customer Details</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="text-xs text-gray-600 space-y-2">
          <div class="flex items-center justify-between py-1 border-b border-gray-100">
            <span class="text-gray-500">Customer Name</span>
            <span class="font-bold text-gray-900">{{ customerName }}</span>
          </div>
          <div class="flex items-center justify-between py-1 border-b border-gray-100">
            <span class="text-gray-500">Contact Number</span>
            <span class="font-bold text-indigo-700">{{ customerMobile || '-' }}</span>
          </div>
          <div v-if="customerAddress" class="py-1 border-b border-gray-100">
            <span class="text-gray-500 block mb-0.5">Current Address</span>
            <span class="font-semibold text-blue-600 cursor-pointer flex items-start gap-1" @click="openGoogleMaps">
              <v-icon x-small color="blue" class="mt-0.5">mdi-map-marker</v-icon>
              {{ customerAddress }}
            </span>
          </div>
          <div v-if="taskData && (taskData.crn || taskData.caseNo)" class="flex items-center justify-between py-1">
            <span class="text-gray-500">CRN / Case ID</span>
            <span class="font-bold text-gray-900 font-mono">{{ taskData.crn || taskData.caseNo }}</span>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Panel 3: Account & Loan Details -->
      <v-expansion-panel class="rounded-2xl border border-gray-100 shadow-2xs before:hidden">
        <v-expansion-panel-header class="font-bold text-xs sm:text-sm text-gray-800 py-3">
          <div class="flex items-center gap-2">
            <v-icon small color="indigo">mdi-file-document-outline</v-icon>
            <span>Details</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="text-xs text-gray-600 space-y-2">
          <div v-if="posAmount" class="flex items-center justify-between py-1 border-b border-gray-100">
            <span class="text-gray-500">Principal Outstanding (POS)</span>
            <span class="font-extrabold text-indigo-700">₹ {{ posAmount }}</span>
          </div>
          <div v-if="emiAmount" class="flex items-center justify-between py-1 border-b border-gray-100">
            <span class="text-gray-500">EMI Amount</span>
            <span class="font-extrabold text-teal-700">₹ {{ emiAmount }}</span>
          </div>
          <div v-if="taskData && taskData.bucket"
            class="flex items-center justify-between py-1 border-b border-gray-100">
            <span class="text-gray-500">Bucket</span>
            <span class="font-bold text-amber-700">{{ taskData.bucket }}</span>
          </div>
          <div v-if="taskData && (taskData.caseType || taskData.subCase)"
            class="flex items-center justify-between py-1">
            <span class="text-gray-500">Case Type</span>
            <span class="font-semibold text-gray-800">{{ taskData.caseType || taskData.subCase }}</span>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Bottom Start / Continue Job Action Button Bar -->
    <div class="fixed sm:bottom-6 left-0 right-0 max-w-xl mx-auto px-4 z-30"
      :style="{ bottom: 'calc(4.25rem + var(--safe-bottom, 0px))' }">
      <v-btn depressed block :color="$vuetify.theme.currentTheme.employeePrimary"
        class="h-11 rounded-xl font-extrabold text-xs sm:text-sm white--text shadow-lg" :loading="startingTask"
        :disabled="!isCheckedIn" @click="handleStartJob">
        <v-icon left small>mdi-play</v-icon>
        {{ taskStatus === 'PENDING' ? 'Start Job' : 'Continue' }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  getTaskById,
  getCollectionCaseById,
  updateTaskStatus,
  updateCaseStatus,
  getCheckInStatus,
  getTaskSettings,
  getOrgConfig
} from '@/app/services/dashboard'
import {
  sendStepLocation,
  computeTaskSteps,
  getNextStepRoute
} from '@/app/utils/taskFlow'
import { convertNumberToAmount, getLatLong } from '@/app/utils/helpers'

export default {
  name: 'AppStartJob',
  data() {
    return {
      taskId: this.$route.params.taskId,
      isCollection: this.$route.query.isCollection === 'true' || this.$route.query.isCollection === true,
      taskData: null,
      isCheckedIn: true,
      taskSettings: null,
      orgConfig: null,
      startingTask: false,
      mapInstance: null,
      hasCoordinates: false,
      mapCenter: { lat: 19.076, lng: 72.8777 },
      activePanels: [0, 1, 2],
      stepsList: []
    }
  },
  computed: {
    customerName() {
      return (
        this.taskData?.customer?.customerFullName ||
        this.taskData?.customer?.name ||
        (this.taskData?.customer?.firstName ? `${this.taskData.customer.firstName} ${this.taskData.customer.lastName || ''}`.trim() : '') ||
        this.taskData?.taskTitle ||
        this.taskData?.case?.customer?.customerFullName ||
        'Customer'
      )
    },
    customerMobile() {
      const mob = this.taskData?.customer?.customerMobile || this.taskData?.customer?.mobile || this.taskData?.case?.customer?.customerMobile
      return Array.isArray(mob) ? mob[0] : mob || ''
    },
    caseNumber() {
      return this.taskData?.caseNo ? `#${this.taskData.caseNo}` : (this.taskData?.case?.caseNo ? `#${this.taskData.case.caseNo}` : 'Task')
    },
    taskStatus() {
      return (this.isCollection ? (this.taskData?.caseStatus || this.taskData?.taskStatus || this.taskData?.status) : this.taskData?.status) || 'OPEN'
    },
    clockInWarningVisible() {
      return !this.isCheckedIn && this.taskStatus !== 'COMPLETED'
    },
    appointmentDateTime() {
      const d = this.taskData?.caseStartDate || this.taskData?.createdAt || this.taskData?.taskStartDateTime || this.taskData?.startDate
      return d ? dayjs(d).format('DD/MM/YYYY, hh:mm A') : '-'
    },
    customerAddress() {
      const addrList = this.taskData?.customer?.customerAddress || this.taskData?.case?.customer?.customerAddress
      if (Array.isArray(addrList) && addrList.length > 0) {
        const a = addrList[0]
        return [a.addressLine1, a.addressLine2, a.city, a.state, a.pinCode].filter(Boolean).join(', ')
      }
      return this.taskData?.address || ''
    },
    posAmount() {
      const pos = this.taskData?.pos || this.taskData?.collectionDetails?.principalOutstanding || this.taskData?.case?.pos || this.taskData?.case?.collectionDetails?.principalOutstanding
      return pos ? convertNumberToAmount(pos) : null
    },
    emiAmount() {
      const emi = this.taskData?.emiAmount || this.taskData?.collectionDetails?.emiAmount || this.taskData?.case?.emiAmount
      return emi ? convertNumberToAmount(emi) : null
    }
  },
  mounted() {
    this.initData()
  },
  methods: {
    ensureGoogleMapsLoaded() {
      if (window.google && window.google.maps) {
        return Promise.resolve()
      }
      return new Promise((resolve) => {
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
        if (existingScript) {
          const checkTimer = setInterval(() => {
            if (window.google && window.google.maps) {
              clearInterval(checkTimer)
              resolve()
            }
          }, 200)
          return
        }

        const script = document.createElement('script')
        const apiKey = process.env.VUE_APP_MAP_KEY || ''
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=Function.prototype`
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = (err) => console.error('Error loading Google Maps API:', err)
        document.head.appendChild(script)
      })
    },
    async initData() {
      try {
        const [taskRes, checkInRes, settingsRes, orgRes] = await Promise.allSettled([
          this.isCollection
            ? getTaskById({ taskId: this.taskId, isCollection: true }).catch(() => getCollectionCaseById({ caseId: this.taskId }))
            : getTaskById({ taskId: this.taskId, isCollection: false }),
          getCheckInStatus(),
          getTaskSettings(),
          getOrgConfig()
        ])

        if (taskRes.status === 'fulfilled') {
          const res = taskRes.value.data
          this.taskData = res?.case || res?.task || res?.data?.case || res?.data?.task || res?.data || res
        }

        if (checkInRes.status === 'fulfilled') {
          const status = checkInRes.value.data?.data?.status || checkInRes.value.data?.status
          this.isCheckedIn = status === 'CHECKED_IN'
        }

        if (settingsRes.status === 'fulfilled') {
          const s = settingsRes.value.data
          this.taskSettings = s?.data || s?.taskSettings || s
        }

        if (orgRes.status === 'fulfilled') {
          const o = orgRes.value.data
          this.orgConfig = o?.data || o?.orgConfig || o
        }

        this.stepsList = computeTaskSteps(this.taskSettings, this.orgConfig)

        await this.ensureGoogleMapsLoaded()
        await this.$nextTick()
        this.renderMap()
      } catch (err) {
        console.error('Error loading task details:', err)
      }
    },
    renderMap() {
      if (!window.google || !window.google.maps) return
      const mapEl = document.getElementById('start-job-google-map')
      if (!mapEl) return

      // Look for coordinates in customer address or taskData
      const addr = this.taskData?.customer?.customerAddress?.[0]
      const lat = addr?.coordinates?.latitude || addr?.latitude || this.taskData?.latitude || 19.076
      const lng = addr?.coordinates?.longitude || addr?.longitude || this.taskData?.longitude || 72.8777

      this.mapCenter = { lat: Number(lat), lng: Number(lng) }
      this.hasCoordinates = true

      this.mapInstance = new window.google.maps.Map(mapEl, {
        center: this.mapCenter,
        zoom: 14,
        disableDefaultUI: true,
        zoomControl: true
      })

      new window.google.maps.Marker({
        position: this.mapCenter,
        map: this.mapInstance,
        title: this.customerName
      })
    },
    openGoogleMaps() {
      if (this.customerAddress) {
        const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.customerAddress)}`
        window.open(url, '_blank')
      }
    },
    async handleStartJob() {
      try {
        this.startingTask = true

        let coords = { latitude: 0, longitude: 0 }
        try {
          const pos = await getLatLong()
          coords = pos.coords
        } catch (e) {
          console.warn(e)
        }

        const validTaskId =
          this.taskData?._id ||
          this.taskData?.taskId ||
          this.taskData?.case?.tasks?.[0]?._id ||
          this.taskId

        // 1. Update task to IN_PROGRESS
        if (this.isCollection && (this.taskData?.case?._id || this.taskData?._id)) {
          await updateCaseStatus({
            caseId: this.taskData.case?._id || this.taskData._id,
            status: 'IN_PROGRESS'
          }).catch((e) => console.warn(e))
        }

        await updateTaskStatus({
          taskId: validTaskId,
          isCollection: this.isCollection,
          taskStatus: 'IN_PROGRESS',
          actualStartDateTime: dayjs(),
          additionalInfo: {
            taskStartData: {
              datetime: dayjs(),
              address: this.customerAddress,
              coords
            }
          }
        }).catch((e) => console.warn(e))

        if (this.customerAddress) {
          localStorage.setItem(`taskAddress-${this.taskId}`, this.customerAddress)
          localStorage.setItem(`taskAddress-${validTaskId}`, this.customerAddress)
        }
        localStorage.setItem(`taskStartTime-${this.taskId}`, dayjs().toISOString())
        localStorage.setItem(`taskStartTime-${validTaskId}`, dayjs().toISOString())

        // 2. Send location telemetry
        await sendStepLocation('AppStartJob', this.taskData)

        // 3. Move to next sequence step
        const nextRouteName = getNextStepRoute('AppStartJob', this.stepsList, this.taskData)
        this.$router.push({
          name: nextRouteName,
          params: { taskId: validTaskId },
          query: { isCollection: this.isCollection }
        })
      } catch (err) {
        console.error('Error starting task:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to start task')
      } finally {
        this.startingTask = false
      }
    },
    goBack() {
      this.$router.push('/app/dashboard')
    }
  }
}
</script>
